import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { BarChart3, TrendingUp, Download, Calendar, Filter } from 'lucide-react';

const AnalyticsPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const [timeRange, setTimeRange] = useState('7d');
  const speedChartRef = useRef(null);
  const tempChartRef = useRef(null);

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  // Generate historical data
  const generateHistoricalData = (days) => {
    const data = [];
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    for (let i = days; i >= 0; i--) {
      data.push({
        date: new Date(now - i * dayMs),
        speed: 8 + Math.random() * 4,
        temp: 20 + Math.random() * 5,
        distance: 180 + Math.random() * 40,
      });
    }
    return data;
  };

  const historicalData = generateHistoricalData(timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90);

  useEffect(() => {
    const drawChart = (canvasRef, data, color, label, thresholds) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const width = canvas.width = canvas.offsetWidth * 2;
      const height = canvas.height = 400;
      ctx.scale(2, 2);

      ctx.clearRect(0, 0, width / 2, height / 2);

      // Background
      ctx.fillStyle = 'rgba(6, 10, 18, 0.4)';
      ctx.fillRect(0, 0, width / 2, height / 2);

      const max = Math.max(...data);
      const min = Math.min(...data);
      const range = max - min || 1;
      const padding = 20;
      const chartHeight = height / 2 - padding * 2;

      // Draw threshold zones if provided
      if (thresholds) {
        const { optimal, caution, critical } = thresholds;
        
        // Critical zone (red)
        if (critical) {
          const criticalY = padding + chartHeight - ((critical.max - min) / range) * chartHeight;
          ctx.fillStyle = 'rgba(239, 68, 68, 0.1)';
          ctx.fillRect(padding, padding, width / 2 - padding * 2, criticalY - padding);
        }

        // Caution zone (yellow)
        if (caution) {
          const cautionYTop = padding + chartHeight - ((caution.max - min) / range) * chartHeight;
          const cautionYBottom = padding + chartHeight - ((caution.min - min) / range) * chartHeight;
          ctx.fillStyle = 'rgba(251, 191, 36, 0.1)';
          ctx.fillRect(padding, cautionYTop, width / 2 - padding * 2, cautionYBottom - cautionYTop);
        }

        // Optimal zone (green)
        if (optimal) {
          const optimalYTop = padding + chartHeight - ((optimal.max - min) / range) * chartHeight;
          const optimalYBottom = padding + chartHeight - ((optimal.min - min) / range) * chartHeight;
          ctx.fillStyle = 'rgba(16, 185, 129, 0.15)';
          ctx.fillRect(padding, optimalYTop, width / 2 - padding * 2, optimalYBottom - optimalYTop);
          
          // Optimal range lines
          ctx.strokeStyle = 'rgba(16, 185, 129, 0.6)';
          ctx.lineWidth = 1;
          ctx.setLineDash([5, 3]);
          ctx.beginPath();
          ctx.moveTo(padding, optimalYTop);
          ctx.lineTo(width / 2 - padding, optimalYTop);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(padding, optimalYBottom);
          ctx.lineTo(width / 2 - padding, optimalYBottom);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      // Grid
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 5; i++) {
        const y = (i / 5) * (height / 2);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width / 2, y);
        ctx.stroke();
      }

      // Data line
      const chartWidth = width / 2 - padding * 2;
      const stepX = chartWidth / (data.length - 1);

      ctx.beginPath();
      data.forEach((value, idx) => {
        const x = padding + idx * stepX;
        const y = padding + chartHeight - ((value - min) / range) * chartHeight;
        if (idx === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Area fill
      ctx.lineTo(padding + chartWidth, padding + chartHeight);
      ctx.lineTo(padding, padding + chartHeight);
      ctx.closePath();
      const gradient = ctx.createLinearGradient(0, 0, 0, height / 2);
      gradient.addColorStop(0, color.replace('1)', '0.3)'));
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fill();

      // Data points with color coding
      data.forEach((value, idx) => {
        const x = padding + idx * stepX;
        const y = padding + chartHeight - ((value - min) / range) * chartHeight;
        
        // Determine point color based on thresholds
        let pointColor = color;
        if (thresholds) {
          if (thresholds.critical && value > thresholds.critical.max) {
            pointColor = 'rgba(239, 68, 68, 1)'; // Red
          } else if (thresholds.caution && (value < thresholds.caution.min || value > thresholds.caution.max)) {
            pointColor = 'rgba(251, 191, 36, 1)'; // Yellow
          } else if (thresholds.optimal && value >= thresholds.optimal.min && value <= thresholds.optimal.max) {
            pointColor = 'rgba(16, 185, 129, 1)'; // Green
          }
        }
        
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = pointColor;
        ctx.fill();
      });
    };

    // Speed chart thresholds: optimal 8-10 kn, caution 6-12 kn, critical >15 kn
    const speedThresholds = {
      optimal: { min: 8, max: 10 },
      caution: { min: 6, max: 12 },
      critical: { max: 15 }
    };

    // Temperature thresholds: optimal 20-25°C, caution 18-27°C
    const tempThresholds = {
      optimal: { min: 20, max: 25 },
      caution: { min: 18, max: 27 }
    };

    drawChart(speedChartRef, historicalData.map(d => d.speed), 'rgba(55, 211, 255, 1)', 'Speed', speedThresholds);
    drawChart(tempChartRef, historicalData.map(d => d.temp), 'rgba(16, 185, 129, 1)', 'Temperature', tempThresholds);
  }, [historicalData]);

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="analytics-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/analytics" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Historical Data & Analytics</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" />
                Export Report
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">Time-series analysis, trends, and data comparisons</p>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6 flex items-center gap-3" data-testid="time-range-selector">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Time Range:</span>
          <div className="flex gap-2">
            {['7d', '30d', '90d'].map((range) => (
              <Button
                key={range}
                variant={timeRange === range ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTimeRange(range)}
                data-testid={`time-range-${range}`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="analytics-page-grid">
          {/* System Health Overview */}
          <Card className="col-span-12" data-testid="system-health-card">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">System Performance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-emerald-400 animate-pulse"></div>
                    <span className="text-sm">Speed: Optimal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-emerald-400"></div>
                    <span className="text-sm">Temperature: Normal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-emerald-400"></div>
                    <span className="text-sm">Efficiency: Excellent</span>
                  </div>
                </div>
                <Badge variant="outline" className="border-emerald-400 text-emerald-400 bg-emerald-500/10">
                  All Systems Operational
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <Card className="col-span-12 lg:col-span-3" data-testid="stat-avg-speed">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Speed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">9.2 kn</div>
              <div className="flex items-center gap-1 text-sm text-emerald-400 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>+2.3% vs prev period</span>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-12 lg:col-span-3" data-testid="stat-distance">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Distance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">1,547 nm</div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <Calendar className="h-3 w-3" />
                <span>Last {timeRange === '7d' ? '7' : timeRange === '30d' ? '30' : '90'} days</span>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-12 lg:col-span-3" data-testid="stat-avg-temp">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Temperature</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">22.4 °C</div>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                <span>Surface water</span>
              </div>
            </CardContent>
          </Card>

          <Card className="col-span-12 lg:col-span-3" data-testid="stat-efficiency">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">Efficiency</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold font-mono">94.2%</div>
              <div className="flex items-center gap-1 text-sm text-emerald-400 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>Excellent</span>
              </div>
            </CardContent>
          </Card>

          {/* Speed Chart */}
          <Card className="col-span-12 xl:col-span-6" data-testid="speed-chart-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Vessel Speed Over Time</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-emerald-400/20 border border-emerald-400"></div>
                    <span>Optimal (8-10 kn)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-amber-400/20 border border-amber-400"></div>
                    <span>Caution (6-12 kn)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-destructive/20 border border-destructive"></div>
                    <span>Critical (&gt;15 kn)</span>
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <canvas ref={speedChartRef} className="w-full" style={{ height: '200px' }} data-testid="speed-chart-canvas" role="img" aria-label="Line chart showing vessel speed over time with optimal range indicators" />
            </CardContent>
          </Card>

          {/* Temperature Chart */}
          <Card className="col-span-12 xl:col-span-6" data-testid="temp-chart-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Surface Temperature Trends</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-emerald-400/20 border border-emerald-400"></div>
                    <span>Optimal (20-25°C)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-amber-400/20 border border-amber-400"></div>
                    <span>Caution (&lt;18 or &gt;27°C)</span>
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <canvas ref={tempChartRef} className="w-full" style={{ height: '200px' }} data-testid="temp-chart-canvas" role="img" aria-label="Line chart showing surface temperature trends over time with optimal range indicators" />
            </CardContent>
          </Card>

          {/* Data Comparison */}
          <Card className="col-span-12" data-testid="data-comparison-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Performance Comparison</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2" data-testid="comparison-this-period">
                  <h4 className="text-sm font-medium">Current Period</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Avg Speed</span>
                      <span className="font-mono">9.2 kn</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Distance</span>
                      <span className="font-mono">1,547 nm</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Efficiency</span>
                      <span className="font-mono text-emerald-400">94.2%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2" data-testid="comparison-prev-period">
                  <h4 className="text-sm font-medium">Previous Period</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Avg Speed</span>
                      <span className="font-mono">9.0 kn</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Distance</span>
                      <span className="font-mono">1,512 nm</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Efficiency</span>
                      <span className="font-mono">92.1%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2" data-testid="comparison-change">
                  <h4 className="text-sm font-medium">Change</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Speed</span>
                      <span className="font-mono text-emerald-400">+2.2%</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-border/30">
                      <span className="text-muted-foreground">Distance</span>
                      <span className="font-mono text-emerald-400">+2.3%</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-muted-foreground">Efficiency</span>
                      <span className="font-mono text-emerald-400">+2.1%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsPage;
